import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  RebuildScope,
  RoundCreationSettingsUpdate,
} from '../../../data-models/round-creation-settings.data-model';
import { previewRoundCreationSettings } from '../../../services/queries/MaggieRoundCreationSettingsQueries';

/**
 * The preview replans the company from nothing, which is the only run that answers "how many
 * rounds would this rule give me". A preview seeded with today's rounds would match everything
 * it already has and honestly report no change — true, and useless for spot-checking.
 */
export const PREVIEW_SCOPE = RebuildScope.ALL;

/**
 * `draft` is already debounced and is null while the form is invalid, so a half-typed window never
 * reaches the API. Previous results stay on screen while the next one loads, so the numbers do not
 * blink to empty on every keystroke.
 */
export function useRoundCreationPreview(companyId: number | null, draft: RoundCreationSettingsUpdate | null) {
  return useQuery({
    queryKey: ['round-creation-preview', companyId, draft],
    queryFn: () =>
      previewRoundCreationSettings({ companyId: companyId!, scope: PREVIEW_SCOPE, settings: draft! }),
    enabled: companyId !== null && draft !== null,
    placeholderData: keepPreviousData,
    // Read-only by construction on the server; a retry would only slow a wrong answer down.
    retry: false,
  });
}
